<script lang="ts" generic="TStay extends ListStay" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, onMounted } from 'vue'

import { filterAvailableColumns, filterDisplayedColumns } from '@/stay-list/stay-list-table/helpers/columnFilters'
import { ListStay, StayListLineSlotProps } from '@/stay-list/stay-list-table/types'
import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum, StayListColumn, StayListColumns } from '@/store/modules/settings/types'

import StayListColumnSettings from './StayListColumnSettings.vue'

const store = useStore()

const props = withDefaults(defineProps<{
  stayList: TStay[]
  availableColumns: StayListColumns
  mode: ModeEnum
  pmsiType?: PmsiTypeEnum
  automationType?: string
  order?: string
  reverseOrder?: boolean
  isLoading: boolean
}>(), {
  pmsiType: PmsiTypeEnum.MCO,
  automationType: null,
  order: 'id',
  reverseOrder: true,
})
defineSlots<{
  line(props: StayListLineSlotProps<TStay>): void
}>()

const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const userSettings = computed(() => store.state.settings.userSettings)

const canSelectStay = computed(() => !store.state.stayList.stayListParams.searchCreation
  && store.state.stayList.stayListParams.searchEdition === -1)

const displayableColumns = computed(() => filterAvailableColumns(
  props.availableColumns,
  {
    pmsiCountry: pmsiCountry.value,
    mode: props.mode
  }
))
const displayedColumns = computed<StayListColumns>(() => filterDisplayedColumns(displayableColumns.value, userSettings.value.stayListColumns))

onMounted(() => {
  store.commit('resetStaySelection')
})

function setColumns(columns: StayListColumns) {
  const settings = { ...userSettings.value }
  settings.stayListColumns = columns
  store.dispatch('setUserSettings', settings)
}

function toggleSelectedStay(stayId: number) {
  store.dispatch('toggleSelectedStay', stayId)
}

const emit = defineEmits<{
  (e: 'stay-line-highlight', stay: TStay)
  (e: 'order-update', toBeOrderedColumn: string)
}>()
function updateOrder(order: string | null) {
  if (order !== null) {
    emit('order-update', order)
  }
}
function coloredButtonIndex(columnInfo: StayListColumn): number {
  if (columnInfo.order !== props.order) {
    return -1
  }

  return ((!props.reverseOrder && columnInfo.invertedOrder)
    || (props.reverseOrder && !columnInfo.invertedOrder)) ? 1 : 0
}

/**
 * Use this event in a line's handler when current stay needs to be sent to parent for processing
 * TODO: Better typing (see https://team-git.sancare.net/dev/ui/-/merge_requests/2792#note_156850)
 */
function onStayLineHighlight(stay: TStay) {
  emit('stay-line-highlight', stay)
}
</script>

<template>
  <div class="table-container text-center">
    <table class="stay-list-table sancare-data-table stripped">
      <thead>
        <tr class="align-items-center">
          <th
            v-for="(columnInfo, columnName) in displayedColumns"
            :key="'column_'+columnName"
            :name="'column_'+columnName"
            :class="{
              'column-icon': columnInfo.icon,
              'ncp': null === columnInfo.order,
            }"
            @click="updateOrder(columnInfo.order)"
          >
            <div class="column-content">
              <div
                v-if="columnInfo.icon"
                :title="columnInfo.isTranslated ? $t(columnInfo.label) : columnInfo.label"
              >
                <sancare-octicon
                  :name="columnInfo.icon"
                  :width="14"
                  :height="14"
                />
              </div>
              <span v-else>{{ columnInfo.isTranslated ? $t(columnInfo.label) : columnInfo.label }}</span>
              <div v-if="columnInfo.order">
                <svg
                  class="order-button"
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="12"
                  viewBox="0 0 17 20"
                  version="1.1"
                >
                  <path
                    :fill="coloredButtonIndex(columnInfo) === 0 ? 'white' : 'darkgrey'"
                    d="M0 8 l 7 -7 7 7 z"
                  />
                  <path
                    :fill="coloredButtonIndex(columnInfo) === 1 ? 'white' : 'darkgrey'"
                    d="M0 13 l 7 7 7 -7 z"
                  />
                </svg>
              </div>
            </div>
          </th>
          <th class="column-icon">
            <div
              class="column-content"
              :title="$t('stayList.columnSettings.title')"
            >
              <stay-list-column-settings
                id="column-settings"
                :mode="mode"
                :user-columns="Object.keys(displayedColumns)"
                :selectable-columns="displayableColumns"
                @set-columns="setColumns"
              />
            </div>
          </th>
        </tr>
      </thead>
      <tbody v-if="isLoading">
        <tr>
          <td :colspan="Object.keys(displayedColumns).length + 2">
            <div
              name="stayList_loader"
              class="loader loader-lg loader-center"
            />
          </td>
        </tr>
      </tbody>
      <tbody
        v-else-if="stayList && stayList.length"
      >
        <template
          v-for="stay in stayList"
          :key="'stayList_Stay_'+stay.id"
        >
          <slot
            name="line"
            :stay="stay"
            :columns="displayedColumns"
            :selected="store.getters.isStaySelected(stay.id)"
            :can-select-stay="canSelectStay"
            :toggle-selected-stay="toggleSelectedStay"
            :on-stay-line-highlight="onStayLineHighlight"
          />
        </template>
      </tbody>
      <tbody v-else>
        <tr class="stay-list-line">
          <td :colspan="Object.keys(displayedColumns).length + 1">
            Aucun séjour correspondant pour la période donnée
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
